/**
 * Club courant de l'ERP (plateforme multi-clubs, squash_manger_api_v2/docs/MULTITENANT_PLAN.md lot 6 ; contrat dans
 * squash_manger_api_v2/docs/MULTITENANCY.md, « Contrat des clients »).
 *
 * Ordre de résolution : hôte du club (`<slug>.gestion.<domaine>`, ou `<slug>.<domaine racine>` quand
 * `REACT_APP_ROOT_DOMAIN` est réglé — voir services/platformSpace.js), sinon `?club=<slug>` (mémorisé), sinon le club
 * mémorisé (connexion / changement de club), sinon aucun. Sans club, l'API retombe sur son club de repli
 * (Squash 22) : on n'envoie alors pas d'en-tête `X-Club`.
 *
 * Le module ne dépend de rien (pas d'API) : il est importé par le client HTTP et par les écrans.
 */

import { getLeagueSubdomain, getRootDomain } from './platformSpace';

const CURRENT_SLUG_KEY = 'courtly.currentClubSlug';
/** Club du jeton staff (`club { id, slug, name }` de manager-login / switch_club). */
const SESSION_CLUB_KEY = 'courtly.currentClub';
/** Adhésions staff du compte (`clubs[]` de manager-login). */
const STAFF_CLUBS_KEY = 'courtly.clubs';
/** Libellé d'hôte des ERP de club : `<slug>.gestion.<domaine>`. */
const GESTION_HOST_LABEL = 'gestion';
/** Sous-domaines du domaine racine qui ne sont pas des clubs (la Ligue s'y ajoute, voir platformSpace.js). */
const RESERVED_ROOT_LABELS = ['www', 'api', GESTION_HOST_LABEL];

function storageGet(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* stockage indisponible : le club ne vaut que pour cette page */
  }
}

function storageRemove(key) {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function readJson(key) {
  try {
    return JSON.parse(storageGet(key) || 'null');
  } catch {
    return null;
  }
}

/** Slug normalisé (minuscules, lettres / chiffres / tirets), '' s'il est invalide. */
export function normalizeClubSlug(raw) {
  const slug = String(raw ?? '').trim().toLowerCase();
  return /^[a-z0-9][a-z0-9_-]*$/.test(slug) ? slug : '';
}

function currentHostname() {
  try {
    return window.location.hostname || '';
  } catch {
    return '';
  }
}

/**
 * Slug porté par l'hôte : `<slug>.gestion.<domaine>`, ou `<slug>.<domaine racine>` (`REACT_APP_ROOT_DOMAIN`, ex.
 * `my22.mysquash.be` ; le sous-domaine de la Ligue et `www` / `api` / `gestion` n'en sont pas), sinon ''.
 */
export function clubSlugFromHost(hostname = currentHostname(), { rootDomain = getRootDomain(), leagueSubdomain = getLeagueSubdomain() } = {}) {
  const host = String(hostname || '').toLowerCase();
  const labels = host.split('.');
  if (labels.length >= 3 && labels[1] === GESTION_HOST_LABEL) return normalizeClubSlug(labels[0]);
  if (rootDomain && host.endsWith(`.${rootDomain}`)) {
    const sub = host.slice(0, -(rootDomain.length + 1));
    if (sub && !sub.includes('.') && sub !== leagueSubdomain && !RESERVED_ROOT_LABELS.includes(sub)) return normalizeClubSlug(sub);
  }
  return '';
}

function clubSlugFromQuery() {
  try {
    return normalizeClubSlug(new URLSearchParams(window.location.search).get('club'));
  } catch {
    return '';
  }
}

/** Vrai si l'ERP est servi sur l'hôte d'un club (le club ne peut alors pas changer sans changer d'hôte). */
export function isClubHost() {
  return Boolean(clubSlugFromHost());
}

/** Slug du club courant, '' si aucun (repli de l'API). Un `?club=` dans l'URL est mémorisé. */
export function getCurrentClubSlug() {
  const fromHost = clubSlugFromHost();
  if (fromHost) return fromHost;
  const fromQuery = clubSlugFromQuery();
  if (fromQuery) {
    if (storageGet(CURRENT_SLUG_KEY) !== fromQuery) storageSet(CURRENT_SLUG_KEY, fromQuery);
    return fromQuery;
  }
  return normalizeClubSlug(storageGet(CURRENT_SLUG_KEY));
}

/** Oublie le club mémorisé (slug inconnu de l'API). Sans effet sur un hôte de club. */
export function forgetStoredClubSlug() {
  storageRemove(CURRENT_SLUG_KEY);
}

/** En-têtes à ajouter à toute requête vers l'API : `{ 'X-Club': slug }`, ou `{}` sans club. */
export function clubRequestHeaders() {
  const slug = getCurrentClubSlug();
  return slug ? { 'X-Club': slug } : {};
}

/** Ajoute `club=<slug>` à une URL (hubs SignalR, liens publics), inchangée sans club. */
export function withClubQuery(url, slug = getCurrentClubSlug()) {
  if (!slug) return url;
  const [base, hash] = String(url).split('#');
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}club=${encodeURIComponent(slug)}${hash != null ? `#${hash}` : ''}`;
}

/** Club du jeton staff `{ id, slug, name }`, ou null (session antérieure aux clubs). */
export function getSessionClub() {
  const club = readJson(SESSION_CLUB_KEY);
  return club && typeof club === 'object' && club.slug ? club : null;
}

/**
 * Clubs accessibles au compte `[{ club_id, slug, name, role }]` : son seul club pour un compte de club (un compte =
 * un club, staff compris), tous les clubs actifs pour un super-admin de la Ligue (`GET /Player/my_clubs`).
 */
export function getStaffClubs() {
  const clubs = readJson(STAFF_CLUBS_KEY);
  return Array.isArray(clubs) ? clubs.filter((c) => c && normalizeClubSlug(c.slug)) : [];
}

/** Mémorise les clubs accessibles (réponse de `GET /Player/my_clubs`, déjà normalisée). */
export function setStaffClubs(clubs) {
  if (Array.isArray(clubs)) storageSet(STAFF_CLUBS_KEY, JSON.stringify(clubs));
}

/**
 * Mémorise le club du jeton (et, si fournies, les adhésions staff) après connexion / changement de club : il
 * devient le club courant hors hôte de club.
 */
export function setSessionClub(club, clubs) {
  const slug = normalizeClubSlug(club?.slug);
  if (slug) {
    storageSet(SESSION_CLUB_KEY, JSON.stringify({ id: club.id ?? null, slug, name: club.name || slug }));
    storageSet(CURRENT_SLUG_KEY, slug);
  } else {
    storageRemove(SESSION_CLUB_KEY);
  }
  if (Array.isArray(clubs)) storageSet(STAFF_CLUBS_KEY, JSON.stringify(clubs));
}

/** Déconnexion : oublie le club du jeton et les adhésions (le club courant reste, pour la marque de la connexion). */
export function clearSessionClub() {
  storageRemove(SESSION_CLUB_KEY);
  storageRemove(STAFF_CLUBS_KEY);
}

/**
 * URL de l'ERP d'un club : même chemin sur l'hôte du club si l'ERP est servi sur un hôte de club, sinon même
 * origine avec `?club=<slug>`.
 */
export function clubAppUrl(slug, path = '/') {
  const target = normalizeClubSlug(slug);
  const { protocol, host, origin } = window.location;
  if (target && isClubHost()) {
    const [, ...rest] = host.split('.');
    return `${protocol}//${[target, ...rest].join('.')}${path}`;
  }
  return `${origin}${withClubQuery(path, target)}`;
}

/** Clé localStorage propre au club courant (sélections d'écran : saison, sport, filtres…). */
export function clubStorageKey(key) {
  const slug = getCurrentClubSlug();
  return slug ? `${slug}:${key}` : key;
}

/**
 * Lit une préférence propre au club. L'ancienne clé sans club (avant le multi-clubs) est reprise une seule fois,
 * par le premier club qui la lit, puis supprimée : les utilisateurs actuels gardent leur sélection.
 */
export function getClubStorageItem(key) {
  const scoped = clubStorageKey(key);
  const value = storageGet(scoped);
  if (value !== null || scoped === key) return value;
  const legacy = storageGet(key);
  if (legacy !== null) {
    storageSet(scoped, legacy);
    storageRemove(key);
  }
  return legacy;
}

export function setClubStorageItem(key, value) {
  storageSet(clubStorageKey(key), value);
}

export function removeClubStorageItem(key) {
  storageRemove(clubStorageKey(key));
}
